// src/pages/widgets/OrganisationFilter.js
import React, { useState, useEffect, useRef } from 'react';
import './style/OrgFilter.css'

const OrganisationFilter = ({ organisations, selectedOrg, onSelectOrg }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  // Sync search term with selected prop
  useEffect(() => {
    setSearchTerm(selectedOrg === 'All' ? '' : selectedOrg);
  }, [selectedOrg]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset input to selected value if user clicked away
        setSearchTerm(selectedOrg === 'All' ? '' : selectedOrg);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOrg]);

  // 1. Filter organisations based on search term
  const filteredOrgs = organisations.filter(org => 
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 2. Group the *filtered* results
  const groupedOrgs = filteredOrgs.reduce((acc, org) => {
    const type = org.type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(org);
    return acc;
  }, {});

  const typeOrder = ['Head Office Account', 'National Account', 'Board', 'Supplier', 'Utilities', 'Wholesaler', 'Customer'];

  // Helper to get list of types to render (ordered)
  const getOrderedTypes = () => {
    const availableTypes = Object.keys(groupedOrgs);
    const ordered = typeOrder.filter(t => availableTypes.includes(t));
    const others = availableTypes.filter(t => !typeOrder.includes(t)).sort();
    return [...ordered, ...others];
  };

  const handleSelect = (value) => {
    onSelectOrg(value);
    setSearchTerm(value === 'All' ? '' : value);
    setIsOpen(false);
  };

  return (
    <div className="org-filter-widget" ref={wrapperRef}>
      <div className="org-filter-label">
        <span className="org-filter-icon">🏢</span>
        <span className="org-filter-text">Filter by Organisation:</span>
      </div>
      
      <div className="org-search-wrapper">
        <input 
          type="text"
          className="org-filter-select" 
          placeholder="Type to search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />

        {isOpen && (
          <div className="org-dropdown-list">
            <div 
              className={`org-dropdown-item ${selectedOrg === 'All' ? 'selected' : ''}`}
              onClick={() => handleSelect('All')}
            >
              All Organisations
            </div>

            {filteredOrgs.length === 0 && (
              <div className="org-no-results">No matches found</div>
            )}

            {getOrderedTypes().map(type => (
              <div key={type} className="org-group-container">
                <div className="org-group-header">{type}</div>
                {groupedOrgs[type].map(org => (
                  <div 
                    key={org.id} 
                    className={`org-dropdown-item ${selectedOrg === org.name ? 'selected' : ''}`}
                    onClick={() => handleSelect(org.name)}
                  >
                    {org.name}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedOrg !== 'All' && (
        <span className="org-filter-info">
          Showing users for: <strong>{selectedOrg}</strong>
        </span>
      )}
    </div>
  );
};

export default OrganisationFilter;
